import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../Configuration/firebase";
import {
  fetchJoinableEvent,
  joinEvent,
  normalizeCode,
} from "../utils/events";
import { registerPush } from "../utils/push";
import "../CSS/App.css";

// Guest flow: see the events you're already in, and join new ones by code. A
// guest can be in several events at once.
const JoinEvent = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }
      setUser(currentUser);
      const snap = await getDoc(doc(db, "users", currentUser.uid));
      if (snap.exists()) setUserInfo(snap.data());
      setLoading(false);
    });
    return () => unsub();
  }, [auth, navigate]);

  const handleJoin = async () => {
    setJoining(true);
    setError("");
    try {
      const event = await fetchJoinableEvent(code);
      const name =
        [userInfo?.firstName, userInfo?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        userInfo?.email ||
        "Guest";
      await joinEvent({ code: event.code, user, name });
      // The participant doc IS the membership record (queried back by
      // getMyJoinedEvents on any device), so there's nothing to store locally.
      // Register for push so this guest is notified if the host later changes
      // the address (native-only; no-op on web). Non-blocking - joining
      // shouldn't fail just because notifications were declined.
      registerPush(user.uid);
      navigate(`/event/${event.code}`);
    } catch (err) {
      console.error("Error joining event:", err);
      setError(err.message || "Couldn't join that event.");
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: "20px" }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div
      className="container"
      style={{
        width: "80%",
        maxWidth: "400px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <h2 style={{ marginBottom: "4px" }}>Join an Event</h2>

      <p
        style={{
          color: "var(--text-secondary, #777)",
          fontSize: "14px",
          marginTop: 0,
        }}
      >
        Enter a code a friend shared with you to see the address.
      </p>

      <input
        type="text"
        inputMode="text"
        autoCapitalize="characters"
        autoComplete="off"
        value={code}
        onChange={(e) => setCode(normalizeCode(e.target.value))}
        placeholder="ABC123"
        maxLength={6}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "14px",
          fontSize: "24px",
          letterSpacing: "6px",
          textAlign: "center",
          borderRadius: "12px",
          border: "1px solid var(--border-color, #ccc)",
          backgroundColor: "var(--input-bg, #fff)",
          color: "var(--text-primary, #000)",
          marginBottom: "12px",
        }}
      />

      {error && <p style={{ color: "red" }}>{error}</p>}

      <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
        <button
          type="button"
          onClick={handleJoin}
          disabled={joining || code.length !== 6}
          style={{
            padding: "10px 24px",
            border: "none",
            cursor: joining || code.length !== 6 ? "default" : "pointer",
            borderRadius: "5px",
            backgroundColor: code.length === 6 ? "#4CAF50" : "#9ccc9e",
            color: "white",
          }}
        >
          {joining ? "Joining..." : "Join"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            backgroundColor: "gray",
            color: "white",
            padding: "10px 15px",
            border: "none",
            cursor: "pointer",
            borderRadius: "5px",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default JoinEvent;
